import { useEffect, useState } from "react";
import { ShoppingBag, ChevronDown, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, asError } from "@/api/client";

const STATUSES = ["Confirmed", "Processing", "Shipped", "Delivered", "Cancelled"];
const STATUS_COLORS = {
  Confirmed: "text-[var(--green)] border-[var(--green)]",
  Processing: "text-[var(--gold)] border-[var(--gold)]",
  Shipped: "text-blue-700 border-blue-300",
  Delivered: "text-emerald-700 border-emerald-300",
  Cancelled: "text-red-700 border-red-300",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  const load = (reset = true) => {
    if (reset) { setLoading(true); setPage(1); }
    const nextPage = reset ? 1 : page + 1;
    const params = { page: nextPage, limit: 50 };
    if (filter !== "all") params.status = filter;
    api.get("/orders", { params })
      .then(({ data }) => {
        const items = data.items || data;
        setOrders((prev) => reset ? items : [...prev, ...items]);
        setHasMore(!!data.has_more);
        if (!reset) setPage(nextPage);
      })
      .catch(() => toast.error("Failed to load orders"))
      .finally(() => { setLoading(false); setLoadingMore(false); });
  };
  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [filter]);

  const loadMore = () => { if (loadingMore || !hasMore) return; setLoadingMore(true); load(false); };

  const updateStatus = async (orderId, status) => {
    try {
      const { data } = await api.patch(`/orders/${orderId}/status`, { status });
      toast.success(`Status updated → ${status}`);
      setOrders((arr) => arr.map((o) => (o.id === orderId ? data : o)));
      if (detail?.id === orderId) setDetail(data);
    } catch (e) { toast.error(asError(e)); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div data-testid="admin-orders">
      <div className="flex gap-2 flex-wrap mb-5">
        {["all", ...STATUSES].map((s) => (
          <button
            key={s}
            data-testid={`admin-orders-filter-${s.toLowerCase()}`}
            onClick={() => setFilter(s)}
            className={`px-3.5 py-1.5 text-[11px] uppercase tracking-widest font-semibold rounded-full border transition ${
              filter === s ? "bg-[var(--green)] text-white border-[var(--green)]" : "bg-white text-[var(--ink-2)] border-[#e5dec9] hover:border-[var(--green)]"
            }`}
          >
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#e5dec9] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-cream)] text-[var(--ink-3)] uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-4 py-3 w-12">S.No</th>
              <th className="text-left">Order</th>
              <th className="text-left">Customer</th>
              <th className="text-left">Placed</th>
              <th className="text-right">Items</th>
              <th className="text-right">Total</th>
              <th className="text-center">Method</th>
              <th className="text-center">Status</th>
              <th className="px-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-10 text-[var(--ink-3)] kbd text-xs">Loading…</td></tr>}
            {!loading && orders.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-[var(--ink-3)] text-sm">No orders {filter !== "all" ? `with status “${filter}”` : "yet"}.</td></tr>}
            {orders.map((o, idx) => (
              <tr key={o.id} data-testid={`admin-order-row-${o.id}`} className="border-t border-[#f0e7d3] hover:bg-[var(--bg-cream)]/50">
                <td className="px-4 py-3 kbd text-[0.7rem] text-[var(--ink-3)]">{idx + 1}</td>
                <td className="py-3 font-semibold text-[var(--ink)]">{o.order_no}</td>
                <td>
                  <div className="text-[var(--ink)] font-medium">{o.shipping_address?.fullName}</div>
                  <div className="text-[var(--ink-3)] text-xs">{o.user_email}</div>
                </td>
                <td className="text-[var(--ink-2)] text-xs">{fmt(o.created_at)}</td>
                <td className="text-right">{o.items.reduce((s, i) => s + i.qty, 0)}</td>
                <td className="text-right font-semibold text-[var(--green)]">₹{o.total}</td>
                <td className="text-center kbd text-[10px] uppercase">{o.method}</td>
                <td className="text-center">
                  <select
                    value={o.status}
                    onChange={(e) => updateStatus(o.id, e.target.value)}
                    className={`text-[10px] uppercase tracking-widest font-semibold rounded-full border px-2.5 py-1 outline-none ${STATUS_COLORS[o.status] || "border-gray-300"}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-3">
                  <button data-testid={`admin-order-view-${o.id}`} onClick={() => setDetail(o)} className="text-[var(--green)] hover:underline text-xs inline-flex items-center gap-1">
                    View <ArrowRight size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="flex justify-center mt-5">
          <button
            data-testid="admin-orders-load-more"
            onClick={loadMore}
            disabled={loadingMore}
            className="btn-outline disabled:opacity-60"
          >
            {loadingMore ? "Loading…" : "Load More Orders"}
          </button>
        </div>
      )}

      {detail && <OrderDetail order={detail} onClose={() => setDetail(null)} onStatusChange={updateStatus} />}
    </div>
  );
}

function OrderDetail({ order, onClose, onStatusChange }) {
  const a = order.shipping_address || {};
  return (
    <div className="fixed inset-0 z-[80] flex">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative ml-auto bg-white w-full max-w-md h-full overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e5dec9] sticky top-0 bg-white">
          <div>
            <div className="kbd text-[0.6rem] text-[var(--ink-3)]">Order</div>
            <h3 className="font-serif-display text-lg text-[var(--ink)]">{order.order_no}</h3>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <Lbl>Status</Lbl>
            <select
              value={order.status}
              onChange={(e) => onStatusChange(order.id, e.target.value)}
              className="adminInp"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <KV k="Customer" v={a.fullName} />
            <KV k="Email" v={order.user_email} />
            <KV k="Phone" v={a.phone} />
            <KV k="Payment" v={(order.method || "").toUpperCase()} />
          </div>
          <div>
            <Lbl>Ship to</Lbl>
            <div className="text-sm text-[var(--ink-2)] leading-relaxed">
              {a.address}, {a.city}, {a.state} {a.pincode}
            </div>
          </div>
          <div>
            <Lbl>Items</Lbl>
            <div className="space-y-2">
              {order.items.map((i) => (
                <div key={i.id} className="flex justify-between text-sm border-b border-[#f0e7d3] pb-2">
                  <div>
                    <div className="font-semibold">{i.name}</div>
                    <div className="text-[var(--ink-3)] text-xs">{i.weight} · Qty {i.qty}</div>
                  </div>
                  <div className="font-semibold text-[var(--green)]">₹{(i.price * i.qty).toLocaleString("en-IN")}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="border-t border-[#e5dec9] pt-4 space-y-1.5 text-sm">
            <Row k="Subtotal" v={`₹${order.subtotal}`} />
            <Row k="Shipping" v={order.shipping ? `₹${order.shipping}` : "Free"} />
            <Row k="Total" v={`₹${order.total}`} bold />
          </div>
        </div>
        <style>{`.adminInp{width:100%;background:#fff;border:1px solid #e5dec9;border-radius:.375rem;padding:.55rem .75rem;font-size:.875rem;outline:none;font-family:inherit}.adminInp:focus{border-color:var(--green)}`}</style>
      </div>
    </div>
  );
}

function Lbl({ children }) { return <label className="kbd text-[0.6rem] text-[var(--ink-3)] mb-1.5 block">{children}</label>; }
function KV({ k, v }) { return <div><Lbl>{k}</Lbl><div className="text-sm text-[var(--ink)] truncate">{v || "—"}</div></div>; }
function Row({ k, v, bold }) { return <div className="flex justify-between"><span className="text-[var(--ink-3)]">{k}</span><span className={bold ? "font-serif-display text-lg text-[var(--green)]" : "text-[var(--ink)]"}>{v}</span></div>; }
